import { Link } from "react-router";
import { describeError } from "@/api/client";
import { useCanEdit, useWorkspace } from "@/api/session";
import {
  Badge,
  Checkbox,
  DataTable,
  EmptyState,
  PageHeader,
  Select,
  Switch,
  TextField,
  Toolbar,
  Tooltip,
  VisuallyHidden,
  useToast,
  type DataTableColumn,
  type RowAction,
} from "@/components";
import { formatInteger, pluralize } from "@/lib/format";
import { countryName } from "@/lib/regions";
import { useSearchParamsInPlace } from "@/lib/hooks";
import {
  noSyncReason,
  platformLabel,
  useSuppliers,
  useSyncSupplier,
  useUpdateSupplier,
  type Supplier,
  type SupplierStatus,
} from "../api";
import { ContinuityPanel } from "../components/ContinuityPanel";
import { CheckCadence, DifficultyMeter, LastSync, ShipsToBadge, SupplierStatusBadge } from "../components/SupplierBits";
import styles from "./SuppliersPage.module.css";

const STATUS_OPTIONS = [
  { value: "all", label: "Any status" },
  { value: "active", label: "Active" },
  { value: "defunct", label: "Closed" },
  { value: "blocked", label: "Blocks automated access" },
];

function isStatus(value: string | null): value is SupplierStatus {
  return value === "active" || value === "defunct" || value === "blocked";
}

function inactiveRowClass(supplier: Supplier): string | undefined {
  return supplier.status === "active" ? undefined : styles.inactiveRow;
}

function host(url: string): string {
  try {
    return new URL(url).host.replace(/^www\./, "");
  } catch {
    return url;
  }
}

/** The supplier directory: who ships to you, how open their catalogs are, and the sync switch. */
export function SuppliersPage() {
  const [searchParams, setSearchParams] = useSearchParamsInPlace();
  const q = searchParams.get("q") ?? "";
  const shipsToMe = searchParams.get("ships") === "1";
  const openFeed = searchParams.get("feed") === "1";
  const statusParam = searchParams.get("status");
  const status = isStatus(statusParam) ? statusParam : undefined;
  const workspace = useWorkspace();
  const country = workspace?.ship_to.country || workspace?.market_country || "CA";
  const canEdit = useCanEdit();
  const toast = useToast();
  const suppliers = useSuppliers({
    ships_to_me: shipsToMe || undefined,
    open_feed: openFeed || undefined,
    status,
  });
  const update = useUpdateSupplier();
  const sync = useSyncSupplier();

  function setParam(name: string, value: string | null) {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (!value) next.delete(name);
        else next.set(name, value);
        return next;
      },
      { replace: true },
    );
  }

  function toggleSync(supplier: Supplier, enabled: boolean) {
    update.mutate(
      { id: supplier.id, sync_enabled: enabled },
      {
        onSuccess: () =>
          enabled
            ? toast.success(`Sync on for ${supplier.name}`, {
                description: "The first check runs within 10 minutes; after that, as often as its prices change.",
              })
            : toast.success(`Sync off for ${supplier.name}`),
        onError: (error) =>
          toast.error(`Couldn’t change the sync for ${supplier.name}`, { description: describeError(error) }),
      },
    );
  }

  function syncNow(supplier: Supplier) {
    sync.mutate(supplier.id, {
      onSuccess: () =>
        toast.info(`Sync queued for ${supplier.name}`, { description: "Progress shows on the Jobs page." }),
      onError: (error) =>
        toast.error(`Couldn’t queue a sync for ${supplier.name}`, { description: describeError(error) }),
    });
  }

  const columns: DataTableColumn<Supplier>[] = [
    {
      id: "name",
      header: "Supplier",
      width: "wide",
      sortable: true,
      sortValue: (supplier) => supplier.name.toLowerCase(),
      cell: (supplier) => (
        <span className={styles.nameCell}>
          <Link to={`/suppliers/${supplier.id}`} className={styles.name}>
            {supplier.name}
          </Link>
          <span className={styles.host}>{host(supplier.url)}</span>
        </span>
      ),
    },
    {
      id: "country",
      header: "Based in",
      sortable: true,
      sortValue: (supplier) => supplier.country,
      cell: (supplier) =>
        supplier.country.length === 2
          ? countryName(supplier.country === "UK" ? "GB" : supplier.country)
          : supplier.country || "—",
    },
    {
      id: "platform",
      header: "Store",
      sortable: true,
      sortValue: (supplier) => supplier.platform,
      cell: (supplier) => (
        <span className={styles.platform}>
          {platformLabel(supplier.platform)}
          {supplier.has_open_feed ? <span className={styles.feed}>open feed</span> : null}
        </span>
      ),
    },
    {
      id: "ships",
      header: "Ships to you",
      sortable: true,
      sortValue: (supplier) =>
        supplier.ships_to_workspace === true ? 0 : supplier.ships_to_workspace === null ? 1 : 2,
      cell: (supplier) => (
        <span className={styles.badges}>
          <ShipsToBadge value={supplier.ships_to_workspace} country={country} />
          <SupplierStatusBadge status={supplier.status} />
        </span>
      ),
    },
    {
      id: "difficulty",
      header: "Scraping difficulty",
      sortable: true,
      sortValue: (supplier) => supplier.scraping_difficulty,
      cell: (supplier) => <DifficultyMeter value={supplier.scraping_difficulty} />,
    },
    {
      id: "products",
      header: "Listings",
      numeric: true,
      sortable: true,
      sortValue: (supplier) => supplier.product_count ?? 0,
      cell: (supplier) => (supplier.product_count ? formatInteger(supplier.product_count) : "—"),
    },
    {
      id: "sync",
      header: "Sync",
      width: "fit",
      cell: (supplier) =>
        supplier.syncable ? (
          <span className={styles.syncCell}>
            <Switch
              className={styles.switch}
              label={<VisuallyHidden>Sync {supplier.name}</VisuallyHidden>}
              checked={supplier.sync_enabled}
              disabled={!canEdit || supplier.status !== "active"}
              onCheckedChange={(checked) => toggleSync(supplier, checked)}
            />
            <CheckCadence supplier={supplier} />
          </span>
        ) : (
          <Tooltip content={noSyncReason(supplier)}>
            <button type="button" className={styles.noSync}>
              Not available
            </button>
          </Tooltip>
        ),
    },
    {
      id: "last",
      header: "Last sync",
      sortable: true,
      sortValue: (supplier) => supplier.last_sync?.started_at ?? "",
      cell: (supplier) => <LastSync run={supplier.last_sync} />,
    },
  ];

  const rowActions = (supplier: Supplier): RowAction<Supplier>[] => [
    {
      label: "Sync now",
      icon: "refresh",
      onSelect: syncNow,
      disabled: !canEdit || !supplier.syncable || supplier.status !== "active",
    },
  ];

  const term = q.trim().toLowerCase();
  const items = suppliers.data?.items.filter(
    (supplier) =>
      !term ||
      supplier.name.toLowerCase().includes(term) ||
      supplier.url.toLowerCase().includes(term) ||
      countryName(supplier.country === "UK" ? "GB" : supplier.country || "XX")
        .toLowerCase()
        .includes(term),
  );
  const shipping = items?.filter((supplier) => supplier.ships_to_workspace === true).length ?? 0;
  const filtersActive = Boolean(q || shipsToMe || openFeed || status);

  return (
    <div className={styles.page}>
      <PageHeader
        title="Suppliers"
        description="Where materials can be bought, who ships to you, and whose catalogs are kept in sync: each is checked as often as its prices change."
        meta={
          items ? (
            <Badge>
              {pluralize(items.length, "supplier", "suppliers")}
              {filtersActive ? "" : `, ${formatInteger(shipping)} ship to ${countryName(country)}`}
            </Badge>
          ) : null
        }
      />
      <ContinuityPanel />
      <Toolbar label="Filter suppliers">
        <TextField
          label="Search suppliers"
          hideLabel
          placeholder="Search by name or country…"
          type="search"
          size="sm"
          value={q}
          onValueChange={(value) => setParam("q", value || null)}
          className={styles.search}
        />
        <Checkbox
          label={`Ships to ${countryName(country)}`}
          checked={shipsToMe}
          onCheckedChange={(checked) => setParam("ships", checked ? "1" : null)}
        />
        <Checkbox
          label="Open catalog feed"
          checked={openFeed}
          onCheckedChange={(checked) => setParam("feed", checked ? "1" : null)}
        />
        <Select
          label="Status"
          hideLabel
          size="sm"
          options={STATUS_OPTIONS}
          value={status ?? "all"}
          onValueChange={(value) => setParam("status", value && value !== "all" ? value : null)}
        />
      </Toolbar>
      <DataTable
        caption="Suppliers"
        columns={columns}
        rows={items}
        getRowId={(supplier) => supplier.id}
        loading={suppliers.isPending}
        error={suppliers.error}
        onRetry={() => void suppliers.refetch()}
        rowActions={rowActions}
        rowActionsLabel={(supplier) => `Actions for ${supplier.name}`}
        rowClassName={inactiveRowClass}
        defaultSort={{ columnId: "name", direction: "asc" }}
        stickyHeader
        empty={
          <EmptyState
            variant="inline"
            headingLevel={3}
            icon="truck"
            title={filtersActive ? "No suppliers match these filters" : "No suppliers yet"}
            description={
              filtersActive
                ? "Clear a filter to see more suppliers."
                : "The supplier directory loads with the research seed when the app starts."
            }
          />
        }
      />
    </div>
  );
}
